/**
 * AdminPasswordChangePage - UC-011: Admin Password Change Use
 * Allows admin to change their password with validation and security checks
 */
import React, { useState } from 'react';
import { Button, Input, Card } from '../components/ui';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { api } from '../core/api-client';

interface PasswordForm {
  currentPassword: string;
  newPassword: string;
  passwordConfirmation: string;
}

interface ValidationErrors {
  currentPassword?: string;
  newPassword?: string;
  passwordConfirmation?: string;
}

export function AdminPasswordChangePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [form, setForm] = useState<PasswordForm>({
    currentPassword: '',
    newPassword: '',
    passwordConfirmation: ''
  });

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Password strength checks — mirror the backend gate (length + score >= 2
  // across upper / lower / digit / special). Keep this in sync with
  // core.security.check_password_strength on the backend.
  const hasMinLength = form.newPassword.length >= 8;
  const hasUppercase = /[A-Z]/.test(form.newPassword);
  const hasLowercase = /[a-z]/.test(form.newPassword);
  const hasNumber = /\d/.test(form.newPassword);

  const meetsStrengthRequirements = hasMinLength && hasUppercase && hasLowercase && hasNumber;

  const isFormValid =
    form.currentPassword.length > 0 &&
    form.newPassword.length > 0 &&
    form.passwordConfirmation.length > 0 &&
    meetsStrengthRequirements &&
    form.newPassword === form.passwordConfirmation;

  const handleInputChange = (field: keyof PasswordForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    if (!form.currentPassword) {
      newErrors.currentPassword = t('adminPasswordChange.errors.currentRequired');
    }

    if (!form.newPassword) {
      newErrors.newPassword = t('adminPasswordChange.errors.newRequired');
    } else if (!meetsStrengthRequirements) {
      newErrors.newPassword = t('adminPasswordChange.errors.newWeak');
    }

    if (!form.passwordConfirmation) {
      newErrors.passwordConfirmation = t('adminPasswordChange.errors.confirmRequired');
    } else if (form.newPassword !== form.passwordConfirmation) {
      newErrors.passwordConfirmation = t('adminPasswordChange.errors.passwordMismatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      // api-client baseURL already includes /api/v1, so the path is just
      // /users/me/password — anything starting with /api/v1 here would
      // produce /api/v1/api/v1/... and 404. (Was the broken/UC-011 bug.)
      await api.patch('/users/me/password', {
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
        passwordConfirmation: form.passwordConfirmation
      });

      setShowSuccessMessage(true);

      setForm({
        currentPassword: '',
        newPassword: '',
        passwordConfirmation: ''
      });

      toast.success(t('adminPasswordChange.toastSuccess'));

      setTimeout(() => {
        setShowSuccessMessage(false);
        navigate('/admin/profile');
      }, 3000);

    } catch (err: any) {
      console.error('Password change failed:', err);

      if (err?.response?.status === 400) {
        const apiErrors: ValidationErrors = {};

        // Map backend ValidationException messages to specific fields. The
        // backend's update_user_password raises a ValidationException with
        // one of these substrings; we surface them next to the field that
        // caused them.
        const errorMessage = err?.response?.data?.detail || '';
        if (errorMessage.includes('Current password is incorrect')) {
          apiErrors.currentPassword = t('adminPasswordChange.errors.currentIncorrect');
        } else if (errorMessage.includes('Password is too weak') || errorMessage.includes('at least 8 characters')) {
          apiErrors.newPassword = t('adminPasswordChange.errors.newWeak');
        } else if (errorMessage.includes('do not match')) {
          apiErrors.passwordConfirmation = t('adminPasswordChange.errors.passwordMismatch');
        } else {
          toast.error(errorMessage || t('adminPasswordChange.genericError'));
        }

        setErrors(apiErrors);
      } else {
        toast.error(t('adminPasswordChange.genericError'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setForm({
      currentPassword: '',
      newPassword: '',
      passwordConfirmation: ''
    });
    setErrors({});
    navigate('/admin/profile');
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-full overflow-x-hidden">
      <div className="mb-5 sm:mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">{t('adminPasswordChange.title')}</h1>
        <p className="mt-1.5 text-base sm:text-sm text-gray-600">{t('adminPasswordChange.subtitle')}</p>
      </div>

      {showSuccessMessage && (
        <div className="mb-6 p-4 bg-green-50 text-green-700 border border-green-200 rounded-lg">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-base sm:text-sm font-medium">{t('adminPasswordChange.successMessage')}</span>
          </div>
        </div>
      )}

      <Card className="p-5 sm:p-6">
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('adminPasswordChange.currentPasswordLabel')}</label>
              <Input
                type="password"
                value={form.currentPassword}
                onChange={handleInputChange('currentPassword')}
                placeholder={t('adminPasswordChange.currentPasswordPlaceholder')}
                error={errors.currentPassword}
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('adminPasswordChange.newPasswordLabel')}</label>
              <Input
                type="password"
                value={form.newPassword}
                onChange={handleInputChange('newPassword')}
                placeholder={t('adminPasswordChange.newPasswordPlaceholder')}
                error={errors.newPassword}
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('adminPasswordChange.confirmLabel')}</label>
              <Input
                type="password"
                value={form.passwordConfirmation}
                onChange={handleInputChange('passwordConfirmation')}
                placeholder={t('adminPasswordChange.confirmPlaceholder')}
                error={errors.passwordConfirmation}
                disabled={isSubmitting}
              />
            </div>

            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="text-sm font-medium text-gray-900 mb-3">{t('adminPasswordChange.requirementsTitle')}</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className={hasMinLength ? 'text-green-600' : 'text-gray-400'}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className={`text-sm ${hasMinLength ? 'text-green-600' : 'text-gray-500'}`}>
                    {t('adminPasswordChange.minLength')}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={hasUppercase ? 'text-green-600' : 'text-gray-400'}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className={`text-sm ${hasUppercase ? 'text-green-600' : 'text-gray-500'}`}>
                    {t('adminPasswordChange.hasUppercase')}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={hasLowercase ? 'text-green-600' : 'text-gray-400'}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className={`text-sm ${hasLowercase ? 'text-green-600' : 'text-gray-500'}`}>
                    {t('adminPasswordChange.hasLowercase')}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={hasNumber ? 'text-green-600' : 'text-gray-400'}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className={`text-sm ${hasNumber ? 'text-green-600' : 'text-gray-500'}`}>
                    {t('adminPasswordChange.hasNumber')}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <Button
              type="button"
              onClick={handleCancel}
              variant="secondary"
              disabled={isSubmitting}
            >
              {t('adminPasswordChange.cancel')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={!isFormValid || isSubmitting}
            >
              {isSubmitting ? t('adminPasswordChange.submitting') : t('adminPasswordChange.submit')}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

export default AdminPasswordChangePage;
